/**
 * Admin → Screensaver settings
 * ─────────────────────────────────────────────────────────────────────
 * Operator console for the Matrix-rain privacy screensaver:
 *   [STATE]    master on/off
 *   [TIMER]    idle minutes before it engages (1–240)
 *   [COLOR]    signature violet default + presets + custom hex
 *   [MOTION]   calm / normal / fast rain
 *   [OVERLAY]  brand wordmark + clock toggles
 *   [PREVIEW]  launches the real overlay instantly
 *
 * Saving PUTs /api/screensaver/config and broadcasts
 * 've:screensaver:config' so the globally-mounted watcher picks the
 * new settings up without a reload.
 */
import React, { useEffect, useState } from 'react';
import { MonitorPlay, Play, Save, RotateCcw, Clock3, Palette, Gauge, Type } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useTheme } from '@/context/ThemeContext';
import { SCREENSAVER_PRESET_COLORS, resolveScreensaverColor } from '@/components/MatrixScreensaver';
import { WISDOM_QUOTES } from '@/lib/wisdomQuotes';

const DEFAULTS = {
  enabled: true,
  idle_minutes: 15,
  color_preset: 'violet',
  custom_color: '#AE52EA',
  speed: 'normal',
  show_brand: true,
  show_clock: true,
};

const COLOR_LABELS = {
  violet: 'Signature Violet',
  gold: 'Champagne Gold',
  green: 'Matrix Green',
  cyan: 'Cyan',
  crimson: 'Crimson',
  ice: 'Ice White',
};

const IDLE_PRESETS = [5, 10, 15, 30, 60];

export default function ScreensaverAdmin() {
  const { effective } = useTheme();
  const isDark = effective === 'dark';
  const [cfg, setCfg] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /* eslint-disable -- plain async fetch on mount */
  useEffect(() => {
    api.get('/screensaver/config')
      .then(({ data }) => setCfg({ ...DEFAULTS, ...data }))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load screensaver settings'))
      .finally(() => setLoading(false));
  }, []);
  /* eslint-enable */

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const save = async () => {
    const minutes = Number(cfg.idle_minutes);
    if (!Number.isFinite(minutes) || minutes < 1 || minutes > 240) {
      toast.error('Idle timer must be between 1 and 240 minutes.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put('/screensaver/config', { ...cfg, idle_minutes: Math.round(minutes) });
      setCfg({ ...DEFAULTS, ...data.config });
      window.dispatchEvent(new CustomEvent('ve:screensaver:config', { detail: data.config }));
      toast.success('Screensaver settings saved.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save settings');
    } finally {
      setSaving(false);
    }
  };

  const preview = () => {
    // Push the CURRENT (possibly unsaved) form state to the watcher so
    // the operator previews exactly what they're editing…
    window.dispatchEvent(new CustomEvent('ve:screensaver:config', { detail: { ...cfg, enabled: true } }));
    window.dispatchEvent(new Event('ve:screensaver:preview'));
  };

  const activeColor = resolveScreensaverColor(cfg);

  const card = `rounded-lg border p-6 ${isDark ? 'border-white/10 bg-white/[0.03]' : 'border-slate-200 bg-white'}`;
  const eyebrow = 'text-[10px] uppercase tracking-[0.28em] font-bold font-mono';
  const bodyText = isDark ? 'text-white/65' : 'text-slate-600';
  const strongText = isDark ? 'text-white' : 'text-slate-900';

  if (loading) {
    return <div className={`p-8 font-mono text-sm ${bodyText}`} data-testid="screensaver-admin-loading">Loading screensaver settings…</div>;
  }

  return (
    <div className="max-w-4xl space-y-6" data-testid="screensaver-admin-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className={eyebrow} style={{ color: '#D4AF37' }}>[ADMIN · SCREENSAVER]</div>
          <h1 className={`display text-3xl mt-1 ${strongText}`}>Matrix privacy screen</h1>
          <p className={`mt-2 text-sm max-w-xl ${bodyText}`}>
            When a signed-in user is idle, the dashboard is covered with a rain of wisdom
            proverbs in 13 languages. Any input returns them exactly where they were.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={preview} data-testid="screensaver-preview-btn"
            className={`inline-flex items-center gap-2 rounded-md border px-4 py-2 text-xs font-mono uppercase tracking-wider transition-colors ${isDark ? 'border-white/15 text-white hover:border-[var(--ve-violet-bright)]' : 'border-slate-300 text-slate-800 hover:border-[var(--ve-violet)]'}`}>
            <Play size={13} /> Preview now
          </button>
          <button onClick={save} disabled={saving} data-testid="screensaver-save-btn"
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs font-mono uppercase tracking-wider text-white transition-opacity disabled:opacity-50"
            style={{ backgroundColor: 'var(--ve-violet-bright)' }}>
            <Save size={13} /> {saving ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </div>

      {/* STATE + TIMER */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className={card}>
          <div className={eyebrow} style={{ color: 'var(--ve-violet-bright)' }}><MonitorPlay size={12} className="inline mr-1.5 -mt-0.5" />[STATE]</div>
          <div className="mt-4 flex items-center justify-between">
            <div>
              <div className={`text-sm font-semibold ${strongText}`}>Screensaver enabled</div>
              <div className={`text-xs mt-1 ${bodyText}`}>Applies to all signed-in users on this platform.</div>
            </div>
            <button
              role="switch" aria-checked={cfg.enabled}
              data-testid="screensaver-enabled-toggle"
              onClick={() => set({ enabled: !cfg.enabled })}
              className="relative h-6 w-11 rounded-full transition-colors shrink-0"
              style={{ backgroundColor: cfg.enabled ? 'var(--ve-violet-bright)' : (isDark ? 'rgba(255,255,255,0.18)' : '#CBD5E1') }}>
              <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all" style={{ left: cfg.enabled ? '22px' : '2px' }} />
            </button>
          </div>
          <div className={`mt-4 font-mono text-[11px] ${cfg.enabled ? 'text-emerald-500' : bodyText}`} data-testid="screensaver-state-readout">
            status: {cfg.enabled ? 'ARMED' : 'OFF'}
          </div>
        </div>

        <div className={card}>
          <div className={eyebrow} style={{ color: 'var(--ve-violet-bright)' }}><Clock3 size={12} className="inline mr-1.5 -mt-0.5" />[IDLE TIMER]</div>
          <div className={`mt-4 text-sm font-semibold ${strongText}`}>Engage after inactivity</div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {IDLE_PRESETS.map((m) => (
              <button key={m} onClick={() => set({ idle_minutes: m })}
                data-testid={`screensaver-idle-${m}`}
                className={`rounded-md border px-3 py-1.5 text-xs font-mono transition-colors ${Number(cfg.idle_minutes) === m
                  ? 'border-[var(--ve-violet-bright)] text-white'
                  : (isDark ? 'border-white/15 text-white/70 hover:border-white/40' : 'border-slate-300 text-slate-600 hover:border-slate-500')}`}
                style={Number(cfg.idle_minutes) === m ? { backgroundColor: 'var(--ve-violet-bright)' } : {}}>
                {m} min
              </button>
            ))}
            <div className="flex items-center gap-1.5">
              <input
                type="number" min={1} max={240} value={cfg.idle_minutes}
                data-testid="screensaver-idle-input"
                onChange={(e) => set({ idle_minutes: e.target.value })}
                className={`w-20 rounded-md border bg-transparent px-2 py-1.5 text-xs font-mono ${isDark ? 'border-white/15 text-white' : 'border-slate-300 text-slate-800'}`} />
              <span className={`text-xs font-mono ${bodyText}`}>min (1–240)</span>
            </div>
          </div>
        </div>
      </div>

      {/* COLOR */}
      <div className={card}>
        <div className={eyebrow} style={{ color: 'var(--ve-violet-bright)' }}><Palette size={12} className="inline mr-1.5 -mt-0.5" />[RAIN COLOR]</div>
        <div className="mt-4 flex flex-wrap gap-3">
          {Object.entries(SCREENSAVER_PRESET_COLORS).map(([key, hex]) => (
            <button key={key} onClick={() => set({ color_preset: key })}
              data-testid={`screensaver-color-${key}`}
              className={`flex items-center gap-2.5 rounded-md border px-3 py-2 transition-colors ${cfg.color_preset === key
                ? 'border-[var(--ve-violet-bright)]'
                : (isDark ? 'border-white/12 hover:border-white/35' : 'border-slate-200 hover:border-slate-400')}`}>
              <span className="h-5 w-5 rounded-full border border-black/20" style={{ backgroundColor: hex, boxShadow: `0 0 10px ${hex}66` }} />
              <span className={`text-xs font-mono ${strongText}`}>{COLOR_LABELS[key]}</span>
            </button>
          ))}
          {/* Custom */}
          <div className={`flex items-center gap-2.5 rounded-md border px-3 py-2 ${cfg.color_preset === 'custom'
            ? 'border-[var(--ve-violet-bright)]'
            : (isDark ? 'border-white/12' : 'border-slate-200')}`}>
            <input type="color" value={cfg.custom_color}
              data-testid="screensaver-custom-color-picker"
              onChange={(e) => set({ custom_color: e.target.value, color_preset: 'custom' })}
              className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0" />
            <input type="text" value={cfg.custom_color}
              data-testid="screensaver-custom-color-hex"
              onChange={(e) => set({ custom_color: e.target.value, color_preset: 'custom' })}
              onFocus={() => set({ color_preset: 'custom' })}
              className={`w-24 bg-transparent text-xs font-mono outline-none ${strongText}`} />
            <span className={`text-xs font-mono ${bodyText}`}>Custom</span>
          </div>
        </div>
        <div className={`mt-4 font-mono text-[11px] ${bodyText}`} data-testid="screensaver-color-readout">
          active: <span style={{ color: activeColor }}>{activeColor}</span> · {cfg.color_preset}
        </div>
      </div>

      {/* MOTION + OVERLAY */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className={card}>
          <div className={eyebrow} style={{ color: 'var(--ve-violet-bright)' }}><Gauge size={12} className="inline mr-1.5 -mt-0.5" />[MOTION]</div>
          <div className={`mt-4 text-sm font-semibold ${strongText}`}>Rain speed</div>
          <div className="mt-3 flex gap-2">
            {['calm', 'normal', 'fast'].map((s) => (
              <button key={s} onClick={() => set({ speed: s })}
                data-testid={`screensaver-speed-${s}`}
                className={`flex-1 rounded-md border px-3 py-2 text-xs font-mono uppercase tracking-wider transition-colors ${cfg.speed === s
                  ? 'border-[var(--ve-violet-bright)] text-white'
                  : (isDark ? 'border-white/15 text-white/70 hover:border-white/40' : 'border-slate-300 text-slate-600 hover:border-slate-500')}`}
                style={cfg.speed === s ? { backgroundColor: 'var(--ve-violet-bright)' } : {}}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className={card}>
          <div className={eyebrow} style={{ color: 'var(--ve-violet-bright)' }}><Type size={12} className="inline mr-1.5 -mt-0.5" />[OVERLAY]</div>
          <div className="mt-4 space-y-3">
            {[
              { key: 'show_brand', label: 'Centered brand wordmark', testid: 'screensaver-brand-toggle' },
              { key: 'show_clock', label: 'Live clock', testid: 'screensaver-clock-toggle' },
            ].map((row) => (
              <div key={row.key} className="flex items-center justify-between">
                <span className={`text-sm ${strongText}`}>{row.label}</span>
                <button role="switch" aria-checked={cfg[row.key]} data-testid={row.testid}
                  onClick={() => set({ [row.key]: !cfg[row.key] })}
                  className="relative h-6 w-11 rounded-full transition-colors shrink-0"
                  style={{ backgroundColor: cfg[row.key] ? 'var(--ve-violet-bright)' : (isDark ? 'rgba(255,255,255,0.18)' : '#CBD5E1') }}>
                  <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all" style={{ left: cfg[row.key] ? '22px' : '2px' }} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* LANGUAGES readout */}
      <div className={card}>
        <div className={eyebrow} style={{ color: 'var(--ve-violet-bright)' }}>[WISDOM · 13 LANGUAGES]</div>
        <p className={`mt-3 text-xs ${bodyText}`}>
          Each falling column spells a real proverb, readable top → bottom in its native script.
        </p>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="screensaver-languages">
          {WISDOM_QUOTES.map((l) => (
            <span key={l.lang}
              className={`rounded-full border px-3 py-1 text-[11px] font-mono ${isDark ? 'border-white/12 text-white/75' : 'border-slate-200 text-slate-600'}`}>
              {l.name} · {l.quotes.length}
            </span>
          ))}
        </div>
      </div>

      {/* Reset */}
      <button onClick={() => set({ ...DEFAULTS })} data-testid="screensaver-reset-btn"
        className={`inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider ${bodyText} hover:opacity-70 transition-opacity`}>
        <RotateCcw size={12} /> Reset to defaults (violet · 15 min)
      </button>
    </div>
  );
}
